import {
  ChangeEventHandler,
  DetailedHTMLProps,
  KeyboardEventHandler,
  TextareaHTMLAttributes,
} from 'react';
import { uid } from 'uid';
import { classNames } from '../../utils';

interface TextAreaProps
  extends Omit<
    DetailedHTMLProps<
      TextareaHTMLAttributes<HTMLTextAreaElement>,
      HTMLTextAreaElement
    >,
    'className' | 'value' | 'onChange' | 'name' | 'rows'
  > {}

interface Props {
  id?: string;
  title?: string;
  placeholder?: string;
  input?: TextAreaProps;
  value?: string;
  readOnly?: boolean;
  className?: string;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  validation?: {
    status?: 'error' | 'success';
    text?: string;
  };
  name?: string;
  rows?: number;
  disabled?: boolean;
  onCmdEnter?: () => void;
}

export default function TextArea({
  id,
  title,
  placeholder,
  input,
  value,
  readOnly,
  className,
  onChange,
  validation,
  name,
  rows,
  disabled = false,
  onCmdEnter,
}: Props) {
  const _id = id || uid(8);

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    // Check if Cmd/Ctrl+Enter was pressed
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && onCmdEnter) {
      e.preventDefault();
      onCmdEnter();
    }

    // Call the original onKeyDown if it exists in input props
    if (input?.onKeyDown) {
      input.onKeyDown(e);
    }
  };

  return (
    <div>
      {title ? (
        <label
          htmlFor={_id}
          className="block text-sm font-medium leading-6 text-gray-900"
        >
          {title}
        </label>
      ) : (
        <></>
      )}

      <textarea
        id={_id}
        rows={rows}
        cols={50}
        name={name}
        {...input}
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        className={classNames(
          'resize-none',
          'relative mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6',
          validation?.status === 'success' ? 'ring-green-300' : '',
          validation?.status === 'error' ? 'ring-red-300' : '',
          className,
        )}
        placeholder={placeholder}
        readOnly={readOnly}
        aria-invalid={validation?.status === 'error' ? 'true' : undefined}
        aria-describedby={validation?.text}
        disabled={disabled}
      />
      {validation?.text ? (
        <p
          className={classNames(
            'mt-2 text-sm',
            validation?.status === 'error' ? 'text-red-600' : '',
            validation?.status === 'success' ? 'text-green-600' : '',
          )}
          id="email-error"
        >
          {validation.text}
        </p>
      ) : (
        <></>
      )}
    </div>
  );
}
